import { LDateTime } from '@app/components/date/LDateTime';
import { UserLabel } from '@app/components/users/UserLabel';
import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { webApi } from '@mi-front/api-clients';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MovieIcon from '@mui/icons-material/Movie';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';
import { SocialExportUploadStatusChip } from './SocialExportUploadStatusChip';
import { useSocialExportUploadItemCardActions } from './useSocialExportUploadItemCardActions';

const waitingPublicLinkMessage = defineMessage({
  id: 'fragments.socialExport.waitingPublicLink',
  defaultMessage: 'Esperando link público',
});

interface SocialExportUploadItemCardProps {
  upload: webApi.FragmentSocialUploadItemDto;
}

/**
 * Single upload item as a card: platform icon + name, status chip, creator + date, primary button and more actions.
 */
export function SocialExportUploadItemCard({ upload }: SocialExportUploadItemCardProps) {
  const { $t } = useIntl();
  const platform = socialPlatforms.get(upload.platform);

  const isCompleted = upload.status === webApi.FragmentSocialUploadItemDtoStatusEnum.COMPLETED;
  const completedData = upload.data as webApi.UploadCompletedDataDto | undefined;
  const scheduledData = upload.data as webApi.UploadScheduledDataDto | undefined;

  const dateValue = isCompleted
    ? completedData?.publishedAt
    : upload.status === webApi.FragmentSocialUploadItemDtoStatusEnum.SCHEDULED
      ? scheduledData?.publishAt
      : upload.createdAt;

  const hasPublicUrl = Boolean(upload.platformFeatures?.publicUrl);
  const { trigger: actionsTrigger, menuAndDialog: actionsMenuAndDialog } =
    useSocialExportUploadItemCardActions({ upload });

  const viewVideoButton = isCompleted ? (
    hasPublicUrl ? (
      <Button
        component="a"
        href={upload.platformFeatures!.publicUrl}
        target="_blank"
        rel="noreferrer"
        startIcon={<OpenInNewIcon />}
      >
        <FormattedMessage id="fragments.socialExport.viewVideo" defaultMessage="Ver video" />
      </Button>
    ) : (
      <Tooltip title={$t(waitingPublicLinkMessage)} placement="top">
        <Box component="span" display="inline-flex">
          <Button disabled startIcon={<AccessTimeIcon fontSize="small" />}>
            <FormattedMessage id="fragments.socialExport.viewVideo" defaultMessage="Ver video" />
          </Button>
        </Box>
      </Tooltip>
    )
  ) : null;

  const shouldRenderActionsGroup = Boolean(viewVideoButton) || Boolean(actionsTrigger);

  return (
    <Card sx={{ overflow: 'visible' }}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
        >
          <Stack direction="row" alignItems="center" spacing={2} flex={1} minWidth={0}>
            <Stack spacing={0.5} minWidth={0}>
              <Stack direction="row" alignItems="center" gap={1} mb={1}>
                {platform.Icon({ sx: { fontSize: 24 }, colored: true })}
                <Typography variant="subtitle1" fontWeight={500}>
                  {platform.name}
                </Typography>
              </Stack>
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <SocialExportUploadStatusChip status={upload.status} />
                {upload.project && (
                  <Chip
                    sx={{ pl: 0.5 }}
                    icon={<MovieIcon fontSize="small" color="secondary" />}
                    label={
                      <FormattedMessage
                        id="fragments.socialExport.editionWithName"
                        defaultMessage="Edición: {name}"
                        values={{ name: upload.project.name }}
                      />
                    }
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                <Typography component="div" variant="body2" color="text.secondary">
                  <FormattedMessage
                    id="fragments.socialExport.byUser"
                    defaultMessage="Por {user}"
                    values={{
                      user: <UserLabel user={upload.creator} />,
                    }}
                  />
                  {' • '}
                  <LDateTime value={dateValue ?? upload.createdAt} showRelative="inline" />
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" flexShrink={0}>
            {shouldRenderActionsGroup ? (
              <ButtonGroup variant="outlined" size="small">
                {viewVideoButton}
                {actionsTrigger}
              </ButtonGroup>
            ) : null}
            {actionsMenuAndDialog}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
